import React from "react";
import {
  useEditProjectAllocatedResourcesBudget,
  useEditProjectAllocatedExpensesBudget,
} from "@planzone/view-models";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Props } from "./types";
import styles from "./styles.scss";
import { TableRow } from "./tableRow";
import { AllocationUnit } from "@common/allocationUnit";

function toFixed(num: number | string) {
  const nbr = typeof num === "string" ? parseFloat(num) : num;

  return typeof num !== "undefined" && num !== ""
    ? Math.round(nbr) === nbr
      ? nbr.toString()
      : nbr.toFixed(2)
    : "";
}

function PlannedBudget({ synthesis, project }: Props) {
  const { t } = useTranslation();

  const {
    isRequestPending: isEditBudgetResourcesRequestPending,
    editProjectAllocatedResourcesBudget,
  } = useEditProjectAllocatedResourcesBudget(project);

  const {
    isRequestPending: isEditBudgetExpensesRequestPending,
    editProjectAllocatedExpensesBudget,
  } = useEditProjectAllocatedExpensesBudget(project);

  return (
    <div className={styles.plannedBudget}>
      <div className={styles.tableHeader}>
        <h3 className={classNames(styles.label, styles.smallLabel)}>
          {t("projectBudgetSynthesis.plannedBudget.headers.planned")}
        </h3>
        <h3 className={styles.label}>
          {t("projectBudgetSynthesis.plannedBudget.headers.dateMonitoring")}
        </h3>
        <h3 className={styles.label}>
          {t("projectBudgetSynthesis.plannedBudget.headers.total")}
        </h3>
        <h3 className={styles.label}>
          {t("projectBudgetSynthesis.plannedBudget.headers.workload")}
        </h3>
      </div>
      <div className={styles.tableSubHeader}>
        <h3 className={styles.smallLabel}>
          {t("projectBudgetSynthesis.plannedBudget.budgetAllocated")}
        </h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.planned")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.real")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.acquired")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.planned")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.real")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.acquired")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.allocated")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.past")}</h3>
        <h3>{t("projectBudgetSynthesis.plannedBudget.remaining")}</h3>
      </div>
      <TableRow
        synthesis={synthesis}
        rowType="Resources"
        editBudget={(budget) =>
          !isEditBudgetResourcesRequestPending &&
          editProjectAllocatedResourcesBudget({
            project,
            allocatedResourcesBudget: budget,
          })
        }
      />
      <TableRow
        synthesis={synthesis}
        rowType="Expenses"
        editBudget={(budget) =>
          !isEditBudgetExpensesRequestPending &&
          editProjectAllocatedExpensesBudget({
            project,
            allocatedExpensesBudget: budget,
          })
        }
      />
      <div className={styles.tableBody}>
        <div className={`${styles.smallLabel} ${styles.value}`}>
          {Number(synthesis.allocatedBudget) >= 1000
            ? `${toFixed(Number(synthesis.allocatedBudget) / 1000)}k`
            : toFixed(synthesis.allocatedBudget)}{" "}
          €
        </div>

        <div className={styles.value}>
          {Number(synthesis.plannedValueAtDate) >= 1000
            ? `${toFixed(Number(synthesis.plannedValueAtDate) / 1000)}k`
            : toFixed(synthesis.plannedValueAtDate)}{" "}
          €
        </div>
        <div className={styles.value}>
          {Number(synthesis.realCostAtDate) >= 1000
            ? `${toFixed(Number(synthesis.realCostAtDate) / 1000)}k`
            : toFixed(synthesis.realCostAtDate)}{" "}
          €
        </div>
        <div className={styles.value}>
          {Number(synthesis.acquiredValueAtDate) >= 1000
            ? `${toFixed(Number(synthesis.acquiredValueAtDate) / 1000)}k`
            : toFixed(synthesis.acquiredValueAtDate)}{" "}
          €
        </div>
        <div className={styles.value}>
          {Number(synthesis.plannedValue) >= 1000
            ? `${toFixed(Number(synthesis.plannedValue) / 1000)}k`
            : toFixed(synthesis.plannedValue)}{" "}
          €
        </div>
        <div className={styles.value}>
          {Number(synthesis.realCost) >= 1000
            ? `${toFixed(Number(synthesis.realCost) / 1000)}k`
            : toFixed(synthesis.realCost)}{" "}
          €
        </div>
        <div className={styles.value}>
          {Number(synthesis.acquiredValue) >= 1000
            ? `${toFixed(Number(synthesis.acquiredValue) / 1000)}k`
            : toFixed(synthesis.acquiredValue)}{" "}
          €
        </div>
        <div
          className={classNames({
            [styles.warning]:
              Number(synthesis.totalSumOfTimeAllocatedOnTheProject) < 0,
            [styles.value]: true,
          })}
        >
          {toFixed(synthesis.totalSumOfTimeAllocatedOnTheProject)}{" "}
          <AllocationUnit />
        </div>
        <div
          className={classNames({
            [styles.warning]: Number(synthesis.totalOfTimesSpentInProject) < 0,
            [styles.value]: true,
          })}
        >
          {toFixed(synthesis.totalOfTimesSpentInProject)} <AllocationUnit />
        </div>
        <div
          className={classNames({
            [styles.warning]:
              Number(synthesis.totalOfRemainingTimeInProject) < 0,
            [styles.value]: true,
          })}
        >
          {toFixed(synthesis.totalOfRemainingTimeInProject)} <AllocationUnit />
        </div>
      </div>
    </div>
  );
}

export { PlannedBudget };
